import { useEffect, useMemo, useState } from 'react';

import { FilterRecord } from '../services/types/pagination-types';
import { useDebouncedValue } from './useDebouncedValue';
import { useUrlState, UseUrlStateOptions } from './useUrlState';

export type UsePaginationOptions = UseUrlStateOptions;
export type PaginationState = ReturnType<typeof useUrlState>['state'];

export interface PaginationActions {
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setOrdering: (field: string, direction?: 'asc' | 'desc') => void;
  toggleOrdering: (field: string) => void;
  setFilters: (filters: FilterRecord) => void;
  setSearch: (term: string) => void;
  setAll: (updates: Partial<PaginationState>) => void;
  reset: () => void;
  getCurrentPage: () => number;
  getTotalPages: (totalCount: number) => number;
}

/**
 * Paginated table state with URL sync, filters, sorting, and debounced search.
 * Wrapper around useUrlState to provide input management and helper actions.
 */
export const usePagination = (options: UsePaginationOptions = {}) => {
  // 1. Use the pure URL state hook
  const { state, updateUrl, setSearchParams, defaultFilters } =
    useUrlState(options);

  // 2. Add UI state management (Search Input)
  // Initialize from state.search to support deep linking
  const [searchInput, setSearchInput] = useState(state.search);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Sync local input when URL changes externally
  useEffect(() => {
    setSearchInput(state.search);
  }, [state.search]);

  // Sync debounce to URL
  useEffect(() => {
    if (debouncedSearch !== state.search && debouncedSearch === searchInput) {
      updateUrl({ search: debouncedSearch, offset: '0' });
    }
  }, [debouncedSearch, state.search, searchInput, updateUrl]);

  // 3. Construct Actions Wrapper
  const actions: PaginationActions = useMemo(
    () => ({
      setPage: (page: number) => {
        const newOffset = (page - 1) * state.limit;
        updateUrl({ offset: String(newOffset) });
      },

      setLimit: (limit: number) => {
        updateUrl({ limit: String(limit), offset: '0' });
      },

      toggleOrdering: (field: string) => {
        const currentField = state.ordering.replace(/^-/, '');
        const isAsc = !state.ordering.startsWith('-');
        let newOrdering = '';

        if (currentField !== field) {
          newOrdering = field;
        } else if (isAsc) {
          newOrdering = `-${field}`;
        }
        updateUrl({ ordering: newOrdering, offset: '0' });
      },

      setOrdering: (field: string, direction?: 'asc' | 'desc') => {
        if (direction) {
          const desiredOrdering = direction === 'desc' ? `-${field}` : field;
          const newOrdering =
            state.ordering === desiredOrdering ? '' : desiredOrdering;
          updateUrl({ ordering: newOrdering, offset: '0' });
        } else {
          // Use toggle logic
          const currentField = state.ordering.replace(/^-/, '');
          const isAsc = !state.ordering.startsWith('-');
          let newOrdering = '';

          if (currentField !== field) {
            newOrdering = field;
          } else if (isAsc) {
            newOrdering = `-${field}`;
          } else {
            newOrdering = field;
          }
          updateUrl({ ordering: newOrdering, offset: '0' });
        }
      },

      setFilters: (filters: FilterRecord) => {
        const updates: Record<string, string | null> = {};
        Object.entries(filters).forEach(([key, value]) => {
          if (defaultFilters[key]) return; // Skip base filters
          if (value !== undefined && value !== null) {
            updates[key] = Array.isArray(value)
              ? value.join(',')
              : String(value);
          } else if (value === null) {
            // Explicitly mark for deletion (e.g., when switching between mutually exclusive special filters)
            updates[key] = null;
          }
        });
        updateUrl({ ...updates, offset: '0' }, true);
      },

      setSearch: (term: string) => {
        // Explicit search update (bypasses debounce if needed)
        updateUrl({ search: term, offset: '0' });
      },

      setAll: (updates: Partial<PaginationState>) => {
        const urlUpdates: Record<string, string | null | undefined> = {};
        if (updates.limit) urlUpdates.limit = String(updates.limit);
        if (updates.offset !== undefined)
          urlUpdates.offset = String(updates.offset);
        if (updates.ordering !== undefined)
          urlUpdates.ordering = updates.ordering;
        if (updates.search !== undefined) urlUpdates.search = updates.search;

        // Handle filter updates if provided
        if (updates.filters !== undefined) {
          // Serialize filters into URL params
          Object.entries(updates.filters).forEach(([key, value]) => {
            // Skip base filters (they're not stored in URL)
            if (defaultFilters[key]) return;

            if (value !== undefined && value !== null) {
              urlUpdates[key] = Array.isArray(value)
                ? value.join(',')
                : String(value);
            } else {
              // Explicitly clear the filter
              urlUpdates[key] = null;
            }
          });

          // Use replace mode to clear existing filters first
          updateUrl(urlUpdates, true);
        } else {
          updateUrl(urlUpdates);
        }
      },

      reset: () => {
        setSearchParams({}, { replace: true });
      },

      getCurrentPage: () => Math.floor(state.offset / state.limit) + 1,

      getTotalPages: (totalCount: number) =>
        Math.ceil(totalCount / state.limit),
    }),
    [
      state.limit,
      state.offset,
      state.ordering,
      // Removed state.search because it's not used inside useMemo
      updateUrl,
      setSearchParams,
      defaultFilters,
    ],
  );

  return { state, actions, searchInput, setSearchInput };
};
